/*
 * File: SalesOrderGrid.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 16 Nov 2021 1:27:28 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useRollbar } from '@rollbar/react';
import { Col, Row } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import NoResults from 'components/atoms/noResults/noResults';
import { SalesOrderCard } from 'components/atoms/SalesOrderCard';
import { WimsicalError } from 'components/atoms/WimsicalError';
import { usePrevious } from 'hooks/usePrevious';
import { SalesOrderModel } from 'models/SalesOrderModel';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { ReduxState } from 'redux/rootReducer';
import { useGetSalesOrdersQuery } from 'redux/services/useMamaBearService';

/* ****************** interfaces ****************** */

export const SalesOrderGrid = (): JSX.Element => {
  const { acuityContext, selectedCustomerAccount } = useSelector((state: ReduxState) => state.app);

  const {
    selectedCompanyDataAreaId: dataAreaId,
    filterCreatedByUserFullName,
    filterCreatedDateTime,
    filterCustomerPO,
    filterDraftId,
    filterQuoteId,
    filterSalesOrderNumber,
    filterSalesPerson,
    filterStartDate,
    filterEndDate,
    filterStatus
  } = useSelector((state: ReduxState) => state.salesOrders);


  const { filterCustomerId, filterCustomerName } = useSelector((state: ReduxState) => state.customerSelectionFilters);

  const [salesOrders, _setSalesOrders] = useState<SalesOrderModel[]>([]);
  const [offset, _setOffset] = useState(0);
  const [isLoadingMore, _setIsLoadingMore] = useState(false);
  const [isNewSearch, _setIsNewSearch] = useState(false);
  const [totalCount, _setTotalCount] = useState<number | null>(null);
  const [resetResults, _setResetResults] = useState(false);

  const prevDataAreaId = usePrevious(dataAreaId);
  const prevFilterCreatedByUserFullName = usePrevious(filterCreatedByUserFullName);
  const prevFilterCreatedDateTime = usePrevious(filterCreatedDateTime);
  const prevFilterCustomerId = usePrevious(filterCustomerId);
  const prevFilterCustomerName = usePrevious(filterCustomerName);
  const prevFilterCustomerPO = usePrevious(filterCustomerPO);
  const prevFilterDraftId = usePrevious(filterDraftId);
  const prevFilterQuoteId = usePrevious(filterQuoteId);
  const prevFilterSalesOrderNumber = usePrevious(filterSalesOrderNumber);
  const prevFilterSalesPerson = usePrevious(filterSalesPerson);
  const prevFilterStartDate = usePrevious(filterStartDate);
  const prevFilterEndDate = usePrevious(filterEndDate);
  const prevFilterStatus = usePrevious(filterStatus);

  const rollbar = useRollbar();

  /* ****************** API/Hooks ****************** */
  const getSalesOrdersParams = {
    dataAreaId: dataAreaId ? dataAreaId : '',
    divisionId: acuityContext ? acuityContext.selectedCustomer.id : undefined,
    createdByUserNameContains: filterCreatedByUserFullName !== null ? filterCreatedByUserFullName : undefined,
    createdDateTime: filterCreatedDateTime !== null ? filterCreatedDateTime : undefined,
    customerIdContains: filterCustomerId !== null ? filterCustomerId : undefined,
    customerNameContains: filterCustomerName !== null ? filterCustomerName : undefined,
    customerPurchaseOrderNumberContains: filterCustomerPO !== null ? filterCustomerPO : undefined,
    draftIdContains: filterDraftId !== null ? filterDraftId : undefined,
    quoteIdContains: filterQuoteId !== null ? filterQuoteId : undefined,
    salesOrderNumberContains: filterSalesOrderNumber !== null ? filterSalesOrderNumber : undefined,
    salesPersonNameContains: filterSalesPerson !== null ? filterSalesPerson : undefined,
    startDate: filterStartDate !== null ? filterStartDate : undefined,
    endDate: filterEndDate !== null ? filterEndDate : undefined,
    status: filterStatus !== null ? filterStatus : undefined,
    orderByField: 'createdDateTime',
    orderByDirection: 'descending',
    offset
  };

  const {
    data: responseSalesOrders,
    isLoading,
    isFetching,
    isError,
    error
  } = useGetSalesOrdersQuery(getSalesOrdersParams, {
    skip: !dataAreaId
  });

  /* ****************** Functions ****************** */
  const handleScrollNext = (): void => {
    _setOffset(offset + 25);
    _setIsLoadingMore(true);
  };

  /* ****************** JSX ****************** */
  const salesOrdersCardsJSX = salesOrders.map((order) => (
    <Col key={order.id} xs={24} sm={24} md={12} lg={12} xl={8} xxl={6}>
      <SalesOrderCard salesOrder={order} />
    </Col>
  ));

  /* ****************** UseEffects ****************** */
  useEffect(() => {
    // Handles initial load
    if (!salesOrders.length && !isLoading && responseSalesOrders?.data) {
      _setSalesOrders(responseSalesOrders.data);
    }

    // Handles sequential load more scroll
    if (salesOrders.length && !isFetching && isLoadingMore && responseSalesOrders?.data) {
      _setSalesOrders([...salesOrders, ...responseSalesOrders.data]);
      _setIsLoadingMore(false);
    }

    if (isNewSearch && !isFetching && responseSalesOrders?.data && !isLoadingMore) {
      _setIsNewSearch(false);
      _setSalesOrders(responseSalesOrders.data);
    }

    // Handles new search logic set
    if (
      prevFilterCreatedByUserFullName !== filterCreatedByUserFullName ||
      prevFilterCreatedDateTime !== filterCreatedDateTime ||
      prevFilterCustomerId !== filterCustomerId ||
      prevFilterCustomerName !== filterCustomerName ||
      prevFilterCustomerPO !== filterCustomerPO ||
      prevFilterDraftId !== filterDraftId ||
      prevFilterQuoteId !== filterQuoteId ||
      prevFilterSalesOrderNumber !== filterSalesOrderNumber ||
      prevFilterSalesPerson !== filterSalesPerson ||
      prevFilterStatus !== filterStatus ||
      prevFilterStartDate !== filterStartDate ||
      prevFilterEndDate !== filterEndDate ||
      prevDataAreaId != dataAreaId
    ) {
      _setIsNewSearch(true);
      _setOffset(0);
    }

    // Handles new search
    if (isNewSearch && !isFetching && responseSalesOrders?.data) {
      _setSalesOrders(responseSalesOrders.data);
      _setIsNewSearch(false);
    }
  }, [
    filterCreatedByUserFullName,
    filterCreatedDateTime,
    filterCustomerId,
    filterCustomerName,
    filterCustomerPO,
    filterDraftId,
    filterQuoteId,
    filterSalesOrderNumber,
    filterSalesPerson,
    filterStartDate,
    filterEndDate,
    filterStatus,
    isFetching,
    isLoading,
    isLoadingMore,
    isNewSearch,
    prevFilterCreatedByUserFullName,
    prevFilterCreatedDateTime,
    prevFilterCustomerId,
    prevFilterCustomerName,
    prevFilterCustomerPO,
    prevFilterDraftId,
    prevFilterQuoteId,
    prevFilterSalesOrderNumber,
    prevFilterSalesPerson,
    prevFilterStartDate,
    prevFilterEndDate,
    prevFilterStatus,
    responseSalesOrders,
    salesOrders,
    prevDataAreaId,
    dataAreaId
  ]);

  // Reset all local state to handle filter changes
  useEffect(() => {
    if (
      prevFilterCreatedByUserFullName !== filterCreatedByUserFullName ||
      prevFilterCreatedDateTime !== filterCreatedDateTime ||
      prevFilterCustomerId !== filterCustomerId ||
      prevFilterCustomerName !== filterCustomerName ||
      prevFilterCustomerPO !== filterCustomerPO ||
      prevFilterDraftId !== filterDraftId ||
      prevFilterQuoteId !== filterQuoteId ||
      prevFilterSalesOrderNumber !== filterSalesOrderNumber ||
      prevFilterSalesPerson !== filterSalesPerson ||
      prevFilterStatus !== filterStatus ||
      prevFilterStartDate !== filterStartDate ||
      prevFilterEndDate !== filterEndDate ||
      prevDataAreaId != dataAreaId
    ) {
      _setSalesOrders([]);
      _setTotalCount(0);
      _setOffset(0);
      _setResetResults(true);
    }
  }, [
    filterCreatedByUserFullName,
    filterCreatedDateTime,
    filterCustomerId,
    filterCustomerName,
    filterCustomerPO,
    filterDraftId,
    filterQuoteId,
    filterSalesOrderNumber,
    filterSalesPerson,
    filterStartDate,
    filterEndDate,
    filterStatus,
    prevFilterCreatedByUserFullName,
    prevFilterCreatedDateTime,
    prevFilterCustomerId,
    prevFilterCustomerName,
    prevFilterCustomerPO,
    prevFilterDraftId,
    prevFilterQuoteId,
    prevFilterSalesOrderNumber,
    prevFilterSalesPerson,
    prevFilterStartDate,
    prevFilterEndDate,
    prevFilterStatus,
    prevDataAreaId,
    dataAreaId
  ]);

  // Update local state
  useEffect(() => {
    if (!salesOrders.length && responseSalesOrders !== undefined && responseSalesOrders.data.length && !isLoading && !isFetching) {
      _setSalesOrders(responseSalesOrders.data);
      _setResetResults(false);
    } else if (
      !resetResults &&
      salesOrders.length &&
      responseSalesOrders &&
      responseSalesOrders.data.length &&
      !salesOrders.find((order) => order.salesOrderNumber === responseSalesOrders.data[0].salesOrderNumber) &&
      !isLoading &&
      !isFetching
    ) {
      _setSalesOrders([...salesOrders, ...responseSalesOrders.data]);
      _setTotalCount(responseSalesOrders.totalCount);
      _setResetResults(false);
    }
    if (responseSalesOrders) _setTotalCount(responseSalesOrders.totalCount);
  }, [isFetching, isLoading, resetResults, responseSalesOrders, salesOrders]);

  const hasMore = (): boolean => {
    if (salesOrders.length < 25) return false;
    else {
      return salesOrders.length > 25 ? offset < (totalCount ?? 0) : true;
    }
  };

  /* ****************** Render ****************** */
  if (isError) {
    const queryError = error as FetchBaseQueryError;

    rollbar.error(`${queryError.status} - Error fetching sales orders`, queryError, { selectedCustomerId: acuityContext?.selectedCustomer.id });

    return <WimsicalError status={typeof queryError.status === 'number' ? queryError.status : 500} message={''} />;
  }

  if (totalCount === 0 && !isLoading) {
    return <NoResults errorMessage="No Results Were Found..." />;
  }
  if (isFetching && offset === 0) {
    return <BlockLoader direction="loader loader--slideUp" />;
  }

  return (
    <InfiniteScroll
      dataLength={salesOrders.length}
      next={handleScrollNext}
      hasMore={hasMore()}
      loader={
        <div style={{ paddingTop: '30px' }}>
          <BlockLoader direction="loader loader--slideUp" />
        </div>
      }>
      <Row gutter={[16, 16]}>{salesOrdersCardsJSX}</Row>
    </InfiniteScroll>
  );
};
